/**
 * texToHeadingText — TEMPORARY DUPLICATE of the `tex-to-heading-text`
 * module owned by `@sciflow/schema-core`.
 *
 * WHY A COPY AND NOT A DEPENDENCY.
 *
 * The reduction is needed by three trees that cannot import one another:
 * this package (public, MIT, its own yarn root), the converter, and the
 * sfo2 schema packages. The agreed mechanism was to share it as a PUBLISHED
 * npm package, scoped to zero-dependency leaf utilities — but
 * `@sciflow/schema-core@0.1.0` is not on the registry (it has only
 * 0.0.1-beta..0.0.3), so that mechanism was never available. Wiring it up
 * instead as a vendored tarball plus `file:` paths crossed a repository
 * boundary that is deliberately not crossed, and cost roughly a day of CI
 * archaeology across seven consumers and six wiring mechanisms.
 *
 * So it is duplicated instead, deliberately and temporarily.
 *
 * WHEN `@sciflow/schema-core` IS PUBLISHED with this function, delete this
 * file and take the real dependency: that is the intended end state, not a
 * hypothetical. The copies are kept honest meanwhile by a drift check that
 * compares them byte-for-byte against the owning package's original.
 *
 * DO NOT edit this file alone. Change the source copy and re-sync, or the
 * drift check fails — which is the point: five independently hand-rolled
 * reductions already existed across these trees before it was shared, and
 * divergence is the exact failure the shared reduction exists to prevent.
 */
/**
 * texToHeadingText — reduce a TeX string to the shortest form that still
 * shows what the author wrote, for surfaces that must render a heading (or
 * any other title-shaped text) as plain text and cannot render math.
 *
 * ─── Origin ──────────────────────────────────────────────────────────────
 *
 * Promoted from the converter's temporary heading-flattening cleanroom stage,
 * since deleted. That
 * stage used this function to replace a `math` node nested in a `heading`
 * with a text approximation, because the schema of the day rejected math
 * inside heading content at all. Once the schema is widened to admit math in
 * headings (specs 2-3), the *reduction itself* is still needed — every
 * surface that cannot render math (a plain-text TOC entry, a PDF bookmark,
 * an OJS title field, …) still needs an honest text fallback, just applied
 * at export/consumption time instead of at import time. This is the one
 * tested implementation of that reduction; do not re-implement it per format
 * — reimplementing per site is how per-site copies drift from each other.
 *
 * ─── What it does, and what it deliberately does not ───────────────────
 *
 * A math node carrying only TeX (no author-written text) has to have SOME
 * string derived from it, and the only honest question is how much to
 * strip:
 *
 * - Unwraps pure-appearance wrappers (`\mathbf`, `\mathrm`, `\mathit`,
 *   `\mathsf`, `\mathtt`, `\text`, `\textbf`, `\textit`) — up to 4 passes so
 *   nested wrappers collapse. These carry no meaning a plain-text reader of
 *   a title needs. `\mathbf{V}_{\mathbf{set}}` → `V_{set}` → `V_set`.
 * - Collapses a subscript/superscript brace group iff it holds ONLY
 *   `[A-Za-z0-9]+` — deliberately narrow. An earlier version allowed any
 *   brace-free content and turned `\sum_{i=1}^{n}` into `\sum_i=1^n`, which
 *   reads as subscript-`i` followed by "=1" — a MEANING change, not a
 *   cosmetic one. `R_{\alpha}` and the `_{i=1}` in `\sum_{i=1}^{n}` keep
 *   their braces because the content is not purely alphanumeric.
 * - Translates TeX's explicit spacing commands (`\,` `\;` `\!` `\:` `\>`
 *   `\ ` `\quad` `\qquad` `\thinspace` `\enspace`) to a literal space, then
 *   collapses runs. This is the ONE family it is safe to translate: `\ `
 *   means "a space" and nothing else, so there is no wrong-symbol risk.
 * - Writes blackboard bold as the Unicode double-struck letters, because
 *   there the Unicode character IS the letter the author wrote, not a guess:
 *   `\mathbb{R}` -> `ℝ`, `\mathbbm{N}` -> `ℕ`, `\mathds{1}` -> `𝟙` (see
 *   DOUBLE_STRUCK; the Letterlike Symbols exceptions ℂ ℍ ℕ ℙ ℚ ℝ ℤ are used,
 *   and the digits 𝟘-𝟡 exist). A group with any character that has no
 *   double-struck form (a command, an underscore) is left exactly as typed.
 *   Script (`\mathcal`, `\mathscr`), fraktur (`\mathfrak`) and bold
 *   (`\mathbf`, `\boldsymbol`) are deliberately NOT mapped: the PDF fonts do
 *   not draw those Unicode letters reliably (U+1D4AA comes out as a
 *   fallback glyph), so they stay as they are; `\mathbf` keeps unwrapping
 *   to the plain letter.
 * - Leaves every other command verbatim — `\alpha`, `\beta_M`, `\sum`,
 *   `\frac{a}{b}` are never touched. This is a STANDING REFUSAL, not a TODO:
 *   a half-correct TeX→Unicode symbol table would silently render the WRONG
 *   symbol, and a wrong symbol in a title is worse than a visibly unrendered
 *   backslash command. Blackboard bold above is the one decided
 *   exception (a mapping with no wrong-symbol risk: each form is the same
 *   letter in another style, and the fonts draw it). Do not add other symbol
 *   tables without re-litigating this.
 * - Invents nothing: every output character is an input character, minus
 *   unwrapped braces, or the Unicode form of an input letter in a letter-style
 *   command.
 * - Never throws, never returns `undefined`: `''`/`undefined` in → `''` out.
 *
 * ─── The empty-reduction case — a documented tension, not an oversight ──
 *
 * This function can return `''` (e.g. for TeX that reduces to nothing
 * renderable). R1's own invariant elsewhere in this program is "never
 * absent, never an empty title" — which is in direct tension with this
 * function's honest "invents nothing" contract: there is no text to invent
 * if none survives the reduction.
 *
 * This function does NOT resolve that tension. It has never been observed
 * to fire on a real document — `attrs.tex` has always carried real content
 * on every customer case motivating this work — so a fallback would be
 * built for a case with zero observed occurrences. Every caller MUST decide
 * for itself what an empty result means for its own surface (drop the node,
 * as the original converter stage does; fall back to a containing part's
 * heading; refuse the surface's own better answer). Do not assume this
 * function's `''` return can never reach a caller — it can, and the caller
 * owns the policy. (Decided: keep today's drop behaviour — recorded here so a
 * future consumer does not rediscover the tension and invent an inconsistent
 * answer.)
 */

/**
 * Blackboard bold: first code points of the Mathematical Alphanumeric Symbols runs, and the characters Unicode
 * keeps in Letterlike Symbols (U+2100 block) instead, so `\mathbb{R}` is `ℝ` (U+211D) and not an unassigned
 * code point.
 */
const DOUBLE_STRUCK = {
    upper: 0x1d538,
    lower: 0x1d552,
    digit: 0x1d7d8,
    exceptions: { C: 'ℂ', H: 'ℍ', N: 'ℕ', P: 'ℙ', Q: 'ℚ', R: 'ℝ', Z: 'ℤ' } as Readonly<Record<string, string>>,
};

/** `\mathbb{R}`, `\mathbb R`: the command, then a braced run of letters/digits or a single one. */
const BLACKBOARD_BOLD = /\\(mathbbm|mathbb|mathds)(?![A-Za-z])\s*(?:\{([A-Za-z0-9]+)\}|([A-Za-z0-9]))/g;

/** One character in double-struck, or undefined when it has no form. */
const doubleStruck = (ch: string): string | undefined => {
    if (Object.prototype.hasOwnProperty.call(DOUBLE_STRUCK.exceptions, ch)) return DOUBLE_STRUCK.exceptions[ch];
    if (ch >= 'A' && ch <= 'Z') return String.fromCodePoint(DOUBLE_STRUCK.upper + ch.charCodeAt(0) - 65);
    if (ch >= 'a' && ch <= 'z') return String.fromCodePoint(DOUBLE_STRUCK.lower + ch.charCodeAt(0) - 97);
    if (ch >= '0' && ch <= '9') return String.fromCodePoint(DOUBLE_STRUCK.digit + ch.charCodeAt(0) - 48);
    return undefined;
};

/** Replace the blackboard-bold commands by the Unicode letters; a group with an unmappable character stays as typed. */
const mapBlackboardBold = (tex: string): string =>
    tex.replace(BLACKBOARD_BOLD, (whole, _command: string, braced?: string, bare?: string) => {
        let out = '';
        for (const ch of braced ?? bare ?? '') {
            const styled = doubleStruck(ch);
            if (styled === undefined) return whole;
            out += styled;
        }
        return out;
    });

/** Appearance-only wrappers whose braces can be dropped without losing meaning. */
const APPEARANCE_WRAPPERS = /\\(?:mathbf|mathrm|mathit|mathsf|mathtt|text|textbf|textit)\{([^{}]*)\}/g;

/**
 * TeX's explicit spacing commands. These are the ONE family it is safe to
 * translate rather than leave verbatim: `\ ` means "a space" and nothing
 * else, so rendering it as a space cannot produce the wrong symbol — the
 * risk that rules out a general TeX→Unicode table does not exist here.
 */
const SPACING_COMMANDS = /\\(?:[,;!:> ]|quad|qquad|thinspace|enspace)/g;

/**
 * Reduce a TeX string to the shortest form that still shows what the author
 * wrote. Applied repeatedly so nested wrappers collapse
 * (`\mathbf{V}_{\mathbf{set}}` → `V_{set}` → `V_set` needs two passes plus
 * the brace strip below).
 */
export function texToHeadingText(tex: string): string {
    // blackboard bold first, so a wrapper around it unwraps to the Unicode letters
    let out = mapBlackboardBold(String(tex ?? ''));
    for (let i = 0; i < 4; i++) {
        const next = out.replace(APPEARANCE_WRAPPERS, '$1');
        if (next === out) break;
        out = next;
    }
    // A subscript/superscript brace group holding ONLY alphanumerics is noise
    // once the wrapper is gone: `V_{set}` reads better as `V_set`, and in TeX
    // `_{set}` and `_set`... are NOT the same — but `_{abc}` and `_abc` differ
    // only in how many tokens the subscript spans, which for a title's
    // display text is invisible. Restricted to `[A-Za-z0-9]+` deliberately —
    // see the module doc above for why a looser version is a meaning change.
    out = out.replace(/([_^])\{([A-Za-z0-9]+)\}/g, '$1$2');
    // Spacing commands become an actual space — see SPACING_COMMANDS. Collapse
    // afterwards so `\;\ ` does not leave a run of blanks.
    out = out.replace(SPACING_COMMANDS, ' ').replace(/  +/g, ' ');
    return out.trim();
}
